import {
  hasAiScriptAccess,
  hasIcmPayrollViewerAccess,
  hasPayrollScriptAccess,
} from "@/core/business/payroll-only";
import {
  canAccessProfitability,
  canOpenProfitabilityReports,
} from "@/profitability/routes";
import { isAdminOrOwner } from "@/user/utils";
import type { IBusiness } from "./business";
import { isProductEnabled } from "./product-access";

interface ProductCapabilityInputs {
  user?: { role?: string; parentable_type?: string } | null;
  business?: Partial<IBusiness> | null;
  settings: unknown;
  documentsOnly?: boolean;
}

// Presentation capabilities only. API authorization remains authoritative;
// callers own loading, onboarding and route-specific recovery behavior.
export const getProductCapabilities = ({
  user,
  business,
  settings,
  documentsOnly = false,
}: ProductCapabilityInputs) => {
  const enabled = {
    payroll: isProductEnabled(settings, "payroll"),
    accounts_payable: isProductEnabled(settings, "accounts_payable"),
    referrals: isProductEnabled(settings, "referrals"),
    profitability: isProductEnabled(settings, "profitability"),
  };
  const canPreviewProducts = isAdminOrOwner(user?.role);
  const hasAiScript = hasAiScriptAccess(business);

  return {
    enabled,
    canPreviewProducts,
    preview: {
      payroll: canPreviewProducts && !enabled.payroll,
      accounts_payable: canPreviewProducts && !enabled.accounts_payable,
    },
    payroll: {
      hasAiScript,
      hasOutput:
        hasAiScript ||
        hasPayrollScriptAccess(business) ||
        hasIcmPayrollViewerAccess(business),
      canEditRules: business?.can_edit_payroll_rules === true,
    },
    profitability: {
      canAccess: canAccessProfitability(user, business),
      canOpenReports: canOpenProfitabilityReports(
        user,
        { ...business, enabled_products: settings },
        documentsOnly,
      ),
    },
  };
};
